(function () {
  const API_ENDPOINT = '/api/warranty/status';
  const STORAGE_KEY = 'mofcoverWarrantyRegistrations';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function readWarrantyRecords() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (error) {
      return [];
    }
  }

  async function mockLookupWarrantyStatus(email, orderNumber) {
    await wait(500 + Math.floor(Math.random() * 301));

    const normalizedEmail = email.trim().toLowerCase();
    const normalizedOrder = orderNumber.trim().toLowerCase();

    return readWarrantyRecords().find((record) => {
      return record.email === normalizedEmail && String(record.orderNumber || '').trim().toLowerCase() === normalizedOrder;
    }) || null;
  }

  function resolveEndpoint(apiBase) {
    const base = String(apiBase || '').trim();

    if (!base) {
      return '';
    }

    return `${base.replace(/\/$/, '')}/status`;
  }

  async function lookupWarrantyStatus(email, orderNumber, apiBase) {
    const endpoint = resolveEndpoint(apiBase);

    if (!endpoint) {
      return mockLookupWarrantyStatus(email, orderNumber);
    }

    const url = new URL(endpoint);
    url.searchParams.set('email', email.trim());
    url.searchParams.set('orderNumber', orderNumber.trim());

    const response = await fetch(url.toString(), {
      method: 'GET'
    });

    const result = await response.json().catch(() => ({}));

    if (response.status === 404) {
      return null;
    }

    if (!response.ok || result.ok === false) {
      throw new Error(result.message || 'Warranty lookup failed.');
    }

    return result.registration || null;
  }

  function setFieldError(form, fieldName, message) {
    const field = form.elements[fieldName];
    const error = form.querySelector(`[data-error-for="${fieldName}"]`);

    if (field) {
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
    }

    if (error) {
      error.textContent = message || '';
      if (message && field) {
        if (!error.id) {
          error.id = `${field.id || fieldName}-error`;
        }
        field.setAttribute('aria-describedby', error.id);
      } else if (field) {
        field.removeAttribute('aria-describedby');
      }
    }
  }

  function showFormError(form, message) {
    const banner = form.querySelector('[data-warranty-status-error]');

    if (!banner) {
      return;
    }

    banner.textContent = message || '';
    banner.hidden = !message;
  }

  function setSubmitting(form, isSubmitting) {
    const submitButton = form.querySelector('[data-warranty-status-submit]');
    const label = form.querySelector('[data-submit-label]');
    const loadingLabel = form.querySelector('[data-submit-loading]');

    if (!submitButton) {
      return;
    }

    submitButton.disabled = isSubmitting;
    submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');

    if (label && loadingLabel) {
      label.hidden = isSubmitting;
      loadingLabel.hidden = !isSubmitting;
    }
  }

  function validateForm(form) {
    const email = String(form.elements.email.value || '').trim();
    const orderNumber = String(form.elements.orderNumber.value || '').trim();
    let isValid = true;

    setFieldError(form, 'email', '');
    setFieldError(form, 'orderNumber', '');

    if (!email) {
      setFieldError(form, 'email', 'Please enter your email address.');
      isValid = false;
    } else if (!EMAIL_PATTERN.test(email)) {
      setFieldError(form, 'email', 'Please enter a valid email address, such as name@example.com.');
      isValid = false;
    }

    if (!orderNumber) {
      setFieldError(form, 'orderNumber', 'Please enter your order number.');
      isValid = false;
    }

    return isValid;
  }

  function setText(resultCard, selector, value) {
    const element = resultCard.querySelector(selector);

    if (element) {
      element.textContent = value || '-';
    }
  }

  function showResult(form, record) {
    const shell = form.closest('.mofcover-warranty__form-shell');
    const resultCard = shell ? shell.querySelector('[data-warranty-status-result]') : null;

    if (!resultCard) {
      return;
    }

    setText(resultCard, '[data-result-status]', record.status || 'Registration Received');
    setText(resultCard, '[data-result-warranty-id]', record.warrantyId);
    setText(resultCard, '[data-result-warranty-term]', record.warrantyTerm || '1-Year Limited Warranty');
    setText(resultCard, '[data-result-order-number]', record.orderNumber);
    setText(resultCard, '[data-result-product-type]', record.productType);
    setText(resultCard, '[data-result-product-size]', record.productSize || 'Not provided');
    setText(resultCard, '[data-result-purchase-channel]', record.purchaseChannel);
    setText(resultCard, '[data-result-purchase-date]', record.purchaseDate);
    setText(resultCard, '[data-result-proof]', record.proofOfPurchaseName ? 'Received' : 'Not uploaded');
    setText(resultCard, '[data-result-submitted]', record.submittedAt ? new Date(record.submittedAt).toLocaleDateString() : '-');

    resultCard.hidden = false;
    resultCard.focus({ preventScroll: true });
    resultCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function initializeWarrantyStatusForm(form) {
    const clearFieldError = (event) => {
      const target = event.target;

      if (target && target.name) {
        setFieldError(form, target.name, '');
        showFormError(form, '');
      }
    };

    form.addEventListener('input', clearFieldError);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      showFormError(form, '');

      if (!validateForm(form)) {
        showFormError(form, 'Please review the highlighted fields before checking your warranty status.');
        const firstInvalid = form.querySelector('[aria-invalid="true"]');
        if (firstInvalid) {
          firstInvalid.focus();
        }
        return;
      }

      setSubmitting(form, true);

      try {
        const record = await lookupWarrantyStatus(form.elements.email.value, form.elements.orderNumber.value, form.dataset.apiBase || '');

        if (!record) {
          showFormError(form, 'We could not find a warranty registration for that email and order number. Please check your information or contact MOFCOVER support.');
          return;
        }

        showResult(form, record);
      } catch (error) {
        showFormError(form, 'We could not check your warranty status right now. Please try again.');
      } finally {
        setSubmitting(form, false);
      }
    });
  }

  window.MOFCOVERWarranty = window.MOFCOVERWarranty || {};
  window.MOFCOVERWarranty.lookupStatus = lookupWarrantyStatus;

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-warranty-status-form]').forEach(initializeWarrantyStatusForm);
  });
})();
